// BackendTTS.ts
'use client';
import type { ITTSEngine, TTSOptions, TTSCapabilities } from './types';

interface CacheEntry {
  audioBuffer: AudioBuffer;
  timestamp: number;
}

interface BackendTTSConfig {
  defaultVoice: string;
  sampleRate: number;
  maxCacheSize: number;
  cacheTTL: number; // Time to live in ms
}

export class BackendTTS implements ITTSEngine {
  private audioContext: AudioContext | null = null;
  private sourceNode: AudioBufferSourceNode | null = null;
  private gainNode: GainNode | null = null;
  private cache = new Map<string, CacheEntry>();
  private currentCallbacks: Partial<TTSOptions> = {};
  private isPlaying = false;
  private config: BackendTTSConfig;

  constructor() {
    this.config = {
      defaultVoice: 'af_bella',
      sampleRate: 22050,
      maxCacheSize: 50,
      cacheTTL: 1000 * 60 * 30, // 30 minutes
    };
  }

  GetEngineName(): string {
    return 'API';
  }

  isAvailable(): boolean {
    return typeof window !== 'undefined' && 'fetch' in window;
  }

  async initialize(): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('BackendTTS not available');
    }

    // Pre-initialize audio context (helps with autoplay policy)
    this.getAudioContext();
  }

  async speak(options: TTSOptions): Promise<void> {
    console.log('debug: backend TTS speak');
    if (this.isPlaying) {
      this.stop();
    }

    this.currentCallbacks = options;
    const cacheKey = this.getCacheKey(options.text, options.voice);

    try {
      // Check cache first
      let audioBuffer = this.getFromCache(cacheKey);

      if (!audioBuffer) {
        // Fetch from backend
        const pcmData = await this.fetchTTS(options.text, options.voice);
        audioBuffer = await this.decodePCM(pcmData);
        this.setCache(cacheKey, audioBuffer);
      }

      // Play audio
      await this.playAudioBuffer(audioBuffer);
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error('TTS playback failed');
      this.currentCallbacks.onError?.(err);
      throw err;
    }
  }

  stop(): void {
    if (this.sourceNode) {
      try {
        this.sourceNode.stop();
        this.sourceNode.disconnect();
      } catch {
        // Ignore if already stopped
      }
      this.sourceNode = null;
    }

    this.isPlaying = false;
  }

  pause(): void {
    this.audioContext?.suspend();
  }

  resume(): void {
    this.audioContext?.resume();
  }

  getCapabilities(): TTSCapabilities {
    return {
      supportsStreaming: false, // Batch playback only
      supportsOffline: false, // Requires network
      maxTextLength: 5000, // Adjust based on your backend limits
    };
  }

  destroy(): void {
    this.stop();
    this.cache.clear();
    this.audioContext?.close();
    this.audioContext = null;
  }

  // Get current playback state
  getPlaybackState(): { isPlaying: boolean; contextState: string } {
    return {
      isPlaying: this.isPlaying,
      contextState: this.audioContext?.state || 'closed',
    };
  }

  // Clear cache
  clearCache(): void {
    this.cache.clear();
  }

  // Get cache stats
  getCacheStats(): { size: number; maxSize: number } {
    return {
      size: this.cache.size,
      maxSize: this.config.maxCacheSize,
    };
  }

  private async fetchTTS(text: string, voice?: string): Promise<ArrayBuffer> {
    const voiceParam = voice || this.config.defaultVoice;

    // Use POST for long text to avoid URL length limits
    const usePost = text.length > 200;

    let response: Response;

    if (usePost) {
      response = await fetch(`/api/v1/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: text,
          voice: voiceParam,
        }),
      });
    } else {
      // GET for short text (your original approach)
      const url = `/api/v1/tts?content=${encodeURIComponent(text)}&voice=${encodeURIComponent(voiceParam)}`;
      response = await fetch(url);
      console.log(
        'debug: fetch the data printing the length of the response:',
        response,
      );
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`TTS API error (${response.status}): ${errorText}`);
    }

    return response.arrayBuffer();
  }

  private async decodePCM(arrayBuffer: ArrayBuffer): Promise<AudioBuffer> {
    const int16Array = new Int16Array(arrayBuffer);
    const float32Array = new Float32Array(int16Array.length);

    // Convert Int16 (-32768 to 32767) to Float32 (-1.0 to 1.0)
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768;
    }

    const audioCtx = this.getAudioContext();
    const audioBuffer = audioCtx.createBuffer(
      1, // mono
      float32Array.length,
      this.config.sampleRate,
    );

    audioBuffer.copyToChannel(float32Array, 0);
    return audioBuffer;
  }

  private async playAudioBuffer(audioBuffer: AudioBuffer): Promise<void> {
    return new Promise((resolve, reject) => {
      const audioCtx = this.getAudioContext();

      // Handle autoplay policy
      if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(reject);
      }

      // Create source and gain nodes
      this.sourceNode = audioCtx.createBufferSource();
      this.gainNode = audioCtx.createGain();

      this.sourceNode.buffer = audioBuffer;

      // Connect: source -> gain -> destination
      this.sourceNode.connect(this.gainNode);
      this.gainNode.connect(audioCtx.destination);

      // Fade in to avoid clicks
      this.gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      this.gainNode.gain.linearRampToValueAtTime(
        1,
        audioCtx.currentTime + 0.01,
      );

      this.isPlaying = true;

      // Event handlers
      this.sourceNode.onended = () => {
        this.isPlaying = false;
        this.sourceNode?.disconnect();
        this.gainNode?.disconnect();
        this.sourceNode = null;
        this.currentCallbacks.onEnd?.();
        resolve();
      };

      // Start playback
      this.sourceNode.start();
      this.currentCallbacks.onStart?.();
    });
  }

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextClass({
        sampleRate: this.config.sampleRate,
      });

      // Handle state changes (e.g., interrupted on iOS)
      this.audioContext.onstatechange = () => {
        console.log('AudioContext state:', this.audioContext?.state);
        if (this.audioContext?.state === 'interrupted') {
          this.audioContext.resume();
        }
      };
    }
    return this.audioContext;
  }

  private getCacheKey(text: string, voice?: string): string {
    return `${voice || this.config.defaultVoice}:${text}`;
  }

  private getFromCache(key: string): AudioBuffer | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check TTL
    const now = Date.now();
    if (now - entry.timestamp > this.config.cacheTTL) {
      this.cache.delete(key);
      return null;
    }

    return entry.audioBuffer;
  }

  private setCache(key: string, audioBuffer: AudioBuffer): void {
    // LRU eviction if cache is full
    if (this.cache.size >= this.config.maxCacheSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      audioBuffer,
      timestamp: Date.now(),
    });
  }
}
